<template>
  <component
    :is="tag"
    ref="trigger"
    :class="['console-tooltip-text', { 'is-clickable': clickable }]"
    :role="clickable ? 'button' : null"
    :tabindex="clickable ? 0 : null"
    @mouseenter="showTooltip"
    @mouseleave="hideTooltip"
    @click="handleClick"
    @keydown.enter.prevent="handleKeyboardClick"
    @keydown.space.prevent="handleKeyboardClick"
  >
    {{ contentText }}
    <transition name="console-tooltip-fade">
      <span
        v-if="visible && tooltipText"
        ref="bubble"
        :class="['console-tooltip-bubble', 'is-' + placement]"
        :style="bubbleStyle"
      >
        {{ tooltipText }}
      </span>
    </transition>
  </component>
</template>

<script>
export default {
  name: 'ConsoleTooltipText',
  props: {
    content: {
      type: [String, Number],
      default: ''
    },
    tooltip: {
      type: [String, Number],
      default: ''
    },
    tag: {
      type: String,
      default: 'span'
    },
    always: {
      type: Boolean,
      default: false
    },
    clickable: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      visible: false,
      placement: 'top',
      bubbleStyle: {
        top: '0px',
        left: '0px'
      }
    }
  },
  computed: {
    contentText () {
      if (this.content === null || this.content === undefined) {
        return ''
      }

      return String(this.content)
    },
    tooltipText () {
      if (this.tooltip !== '' && this.tooltip !== null && this.tooltip !== undefined) {
        return String(this.tooltip)
      }

      return this.contentText
    }
  },
  beforeDestroy () {
    this.unbindWindowEvents()
  },
  methods: {
    showTooltip () {
      if (!this.tooltipText || (!this.always && !this.isOverflowing())) {
        return
      }

      this.visible = true
      this.bindWindowEvents()
      this.$nextTick(this.updatePosition)
    },
    hideTooltip () {
      this.visible = false
      this.unbindWindowEvents()
    },
    handleClick (event) {
      if (!this.clickable) {
        return
      }

      this.$emit('click', event)
    },
    handleKeyboardClick (event) {
      this.handleClick(event)
    },
    bindWindowEvents () {
      window.addEventListener('scroll', this.updatePosition, true)
      window.addEventListener('resize', this.updatePosition)
    },
    unbindWindowEvents () {
      window.removeEventListener('scroll', this.updatePosition, true)
      window.removeEventListener('resize', this.updatePosition)
    },
    isOverflowing () {
      var trigger = this.$refs.trigger

      if (!trigger) {
        return false
      }

      return trigger.scrollWidth > trigger.clientWidth + 1 || trigger.scrollHeight > trigger.clientHeight + 1
    },
    updatePosition () {
      if (!this.visible) {
        return
      }

      var trigger = this.$refs.trigger
      var bubble = this.$refs.bubble

      if (!trigger || !bubble) {
        return
      }

      var triggerRect = trigger.getBoundingClientRect()
      var bubbleRect = bubble.getBoundingClientRect()
      var gap = 14
      var top = triggerRect.top - bubbleRect.height - gap
      var placement = 'top'

      if (top < 12) {
        top = triggerRect.bottom + gap
        placement = 'bottom'
      }

      var left = triggerRect.left + (triggerRect.width - bubbleRect.width) / 2
      var maxLeft = window.innerWidth - bubbleRect.width - 12

      left = Math.max(12, Math.min(left, maxLeft))

      this.placement = placement
      this.bubbleStyle = {
        top: Math.round(top) + 'px',
        left: Math.round(left) + 'px'
      }
    }
  }
}
</script>

<style scoped>
.console-tooltip-text {
  position: relative;
  display: block;
  max-width: 100%;
  overflow: hidden;
  color: inherit;
  line-height: inherit;
  text-overflow: ellipsis;
  vertical-align: top;
  white-space: nowrap;
}

.console-tooltip-text.is-clickable {
  display: inline-block;
  max-width: 100%;
  cursor: pointer;
  transition: color 0.16s ease;
}

.console-tooltip-text.is-clickable:hover,
.console-tooltip-text.is-clickable:focus {
  color: #1677ff;
  outline: none;
}

.console-tooltip-bubble {
  position: fixed;
  z-index: 260;
  max-width: min(360px, calc(100vw - 24px));
  padding: 10px 12px;
  border: 1px solid rgba(189, 212, 255, 0.9);
  border-radius: 14px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(244, 249, 255, 0.98) 100%);
  box-shadow: 0 18px 36px rgba(31, 42, 68, 0.14);
  color: #29406a;
  line-height: 1.6;
  white-space: normal;
  word-break: break-word;
  pointer-events: none;
  backdrop-filter: blur(10px);
}

.console-tooltip-bubble::after {
  content: '';
  position: absolute;
  left: 50%;
  width: 12px;
  height: 12px;
  border-right: 1px solid rgba(189, 212, 255, 0.9);
  border-bottom: 1px solid rgba(189, 212, 255, 0.9);
  background: rgba(249, 252, 255, 0.98);
  transform: translateX(-50%) rotate(45deg);
}

.console-tooltip-bubble.is-top::after {
  bottom: -7px;
}

.console-tooltip-bubble.is-bottom::after {
  top: -7px;
  transform: translateX(-50%) rotate(225deg);
}

.console-tooltip-fade-enter-active,
.console-tooltip-fade-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.console-tooltip-fade-enter,
.console-tooltip-fade-leave-to {
  opacity: 0;
  transform: translateY(4px);
}
</style>



// WEBPACK FOOTER //
// src/components/shared/ConsoleTooltipText.vue